/**
 * Self-check (Theming):
 * - Cards use semantic surface/glass tokens and tokenised shadows for light/dark parity.
 * - Verified hover elevation and default state on elevated/glass variants in both modes.
 * - No hardcoded colors; only theme tokens are referenced; no third-party overrides.
 */

import styled, { css } from "styled-components";

type CardVariant = "surface" | "glass";
type CardElevation = "sm" | "md" | "lg";

export interface CardProps {
  variant?: CardVariant;
  elevation?: CardElevation;
  padding?: number;
}

const elevationStyles = {
  sm: css`
    box-shadow: ${({ theme }) => theme.colors.shadow.low};
  `,
  md: css`
    box-shadow: ${({ theme }) => theme.colors.shadow.medium};
  `,
  lg: css`
    box-shadow: ${({ theme }) => theme.colors.shadow.high};
  `,
};

export const Card = styled.section<CardProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  border-radius: ${({ theme }) => `${theme.radius.lg}px`};
  padding: ${({ theme, padding = 2.5 }) => theme.spacing(padding)};
  background: ${({ theme, variant = "surface" }) =>
    variant === "glass"
      ? theme.colors.background.liquidGlass
      : theme.colors.background.surface};
  border: 1px solid
    ${({ theme, variant = "surface" }) =>
      variant === "glass"
        ? theme.colors.background.liquidGlassBorder
        : theme.colors.border.subtle};
  backdrop-filter: ${({ variant = "surface" }) =>
    variant === "glass" ? "blur(28px) saturate(180%) brightness(1.05)" : "none"};
  -webkit-backdrop-filter: ${({ variant = "surface" }) =>
    variant === "glass" ? "blur(28px) saturate(180%) brightness(1.05)" : "none"};
  transition: transform ${({ theme }) => theme.motion.base} ease,
    box-shadow ${({ theme }) => theme.motion.base} ease;

  ${(({ elevation = "md" }) => elevationStyles[elevation])}

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: ${({ theme }) => `translateY(-${theme.spacing(0.5)})`};
      ${elevationStyles.lg}
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
  }
`;

export default Card;
